import type { SelectOption, SelectFunctionComponents } from '@ztechapps/webui.input'
import { Select, SelectCreatable } from '@ztechapps/webui.input'
import * as React from 'react'
import { useManageTemplate, useReactiveState, useTemplateSelectOptions, useUser } from '@ztechapps/common.hook'
import type { TemplateSelectProps } from './index'
import { useState } from 'react'
import clone from 'lodash/clone'
import merge from 'lodash/merge'
import type { Template } from '@ztechapps/common.contract'

type TemplateSelectCommonProps = Omit<TemplateSelectProps, 'templateScope' | 'templateCategory'> & {
  as: SelectFunctionComponents
  templateSelectOptions?: SelectOption[]
}
const TemplateSelectCommon = ({
  as,
  components,
  placeholder,
  noOptionsMessage,
  templateSelectOptions,
  ...rest
}: TemplateSelectCommonProps) => {
  const Component = as

  const commonProps = {
    options: templateSelectOptions,
    placeholder: placeholder ?? 'Template list.',
    noOptionsMessage: noOptionsMessage ?? (() => 'Cannot find any item from the list.'),
    components,
    backspaceRemovesValue: true,
  }

  return <Component {...rest} {...commonProps} />
}

export type TemplateSelectCreatableProps = Omit<TemplateSelectProps, 'templateCategory'> & { templateCategory: string }
export const TemplateSelect = ({ filter, templateCategory, ...props }: TemplateSelectProps) => {
  const options = useTemplateSelectOptions<SelectOption>({ filter, templateCategory })

  return <TemplateSelectCommon {...props} as={Select} templateSelectOptions={options} />
}

export const TemplateSelectCreatable = ({
  value,
  onChange,
  templateCategory,
  templateScope,
  filter,
  onCreateOption,
  onServerCreateOption,
  ...props
}: TemplateSelectCreatableProps) => {
  const list = useTemplateSelectOptions<SelectOption>({ filter, templateCategory })

  const { user } = useUser()
  const { onAddSubmit } = useManageTemplate({ category: templateCategory })
  const [options, setOptions] = useReactiveState(list)

  const [creatableValue, setCreatableValue] = useState(value)
  const [loading, setLoading] = useState(false)

  const _onCreateOption = async (inputValue: string) => {
    try {
      setLoading(true)
      const originalValue = clone(value)
      const optimisticValue = {
        value: inputValue,
        name: inputValue,
        description: inputValue,
        scope: merge(
          {
            key: 'tenant',
            value: user?.tenantId ?? '',
          },
          templateScope
        ),
      }
      const optimisticItem = {
        ...optimisticValue,
        label: `Creating "${optimisticValue?.value}"`,
        value: optimisticValue?.value,
      }
      if (props.isMulti && Array.isArray(originalValue)) {
        setCreatableValue([...originalValue, optimisticItem])
      } else {
        setCreatableValue(optimisticItem)
      }

      //add it to the list, fire and forget
      const resultTemplate = await onAddSubmit(optimisticValue as unknown as Template)

      setOptions([...options, resultTemplate] as SelectOption[])
      const createdItem = { ...resultTemplate, value: resultTemplate?.value, label: resultTemplate?.value }

      let updatedNewValue
      if (props.isMulti && Array.isArray(originalValue)) {
        updatedNewValue = [...originalValue, createdItem]
      } else {
        updatedNewValue = createdItem
      }

      setCreatableValue(updatedNewValue)

      if (onCreateOption) {
        onCreateOption(inputValue)
      }

      if (onServerCreateOption) {
        onServerCreateOption(updatedNewValue)
      }
    } finally {
      setLoading(false)
    }
  }

  const _onChange = (e, meta) => {
    setCreatableValue(e)

    if (onChange) {
      onChange(e, meta)
    }
  }

  return (
    <TemplateSelectCommon
      templateSelectOptions={options}
      onCreateOption={_onCreateOption}
      onChange={_onChange}
      value={creatableValue}
      isLoading={loading}
      as={SelectCreatable}
      {...props}
    />
  )
}
